(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root) root.KenlinaProductFinder = api;
})(typeof window !== "undefined" ? window : null, function () {
  "use strict";

  const MAX_RESULTS = 3;

  function toNumber(value) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function normalizeWrist(value, unit) {
    const amount = toNumber(value);
    if (amount === null || amount <= 0) return null;
    return unit === "in" ? amount * 2.54 : amount;
  }

  function diameterCategory(mm) {
    if (mm <= 6) return "small";
    if (mm >= 10) return "large";
    return "medium";
  }

  function includesValue(values, selected) {
    return !selected || selected === "any" || values.includes(selected);
  }

  function fitsWrist(product, wristCm) {
    if (wristCm === null) return true;
    return product.fitRanges.some((range) => wristCm >= range[0] && wristCm <= range[1]);
  }

  function eligibility(product, input) {
    const reasons = [];
    if (!product.available) reasons.push("unavailable");
    if (!includesValue(product.formats, input.format)) reasons.push("format");
    if (input.wristRoute && !fitsWrist(product, input.wristCm)) reasons.push("fit");
    if (input.maxBudget !== null && product.price > input.maxBudget) reasons.push("budget");
    if (input.verifiedOnly && input.diameter !== "any" && !product.diameters.length) {
      reasons.push("diameter-unknown");
    }
    if (input.verifiedOnly && input.scent !== "any" && !product.scents.length) {
      reasons.push("scent-unknown");
    }
    return { eligible: reasons.length === 0, reasons };
  }

  function scoreProduct(product, input) {
    let score = 0;
    const reasons = [];
    if (input.format !== "any" && product.formats.includes(input.format)) {
      score += 30;
      reasons.push(`verified ${input.format} format`);
    }
    if (input.wristRoute && fitsWrist(product, input.wristCm)) {
      score += 20;
      reasons.push("verified wrist range");
    }
    if (input.diameter !== "any" && product.diameters.some((mm) => diameterCategory(mm) === input.diameter)) {
      score += 15;
      reasons.push(`verified ${input.diameter} bead diameter`);
    }
    if (input.scent !== "any" && product.scents.includes(input.scent)) {
      score += 15;
      reasons.push(`current ${input.scent} scent wording`);
    }
    if (input.care === "dry" && product.care.includes("dry")) {
      score += 10;
      reasons.push("dry-care guidance matches");
    }
    if (input.context !== "any" && product.contexts.includes(input.context)) {
      score += 5;
      reasons.push(`compatible with ${input.context.replaceAll("-", " ")}`);
    }
    if (input.culturalProfile && typeof window !== "undefined" && window.KenlinaCulturalFinder) {
      const cultural = window.KenlinaCulturalFinder.culturalAdjustment(product, input.culturalProfile);
      score += cultural.score;
      if (cultural.reason) reasons.push(cultural.reason);
    }
    return { score, reasons: reasons.slice(0, 3) };
  }

  function rankProducts(products, rawInput) {
    const input = {
      format: rawInput.format || "any",
      wristRoute: Boolean(rawInput.wristRoute),
      wristCm: normalizeWrist(rawInput.wrist, rawInput.wristUnit),
      diameter: rawInput.diameter || "any",
      scent: rawInput.scent || "any",
      care: rawInput.care || "any",
      context: rawInput.context || "any",
      maxBudget: toNumber(rawInput.maxBudget),
      verifiedOnly: Boolean(rawInput.verifiedOnly),
      culturalProfile: rawInput.culturalProfile || null,
    };
    return products
      .map((product) => ({ product, gate: eligibility(product, input) }))
      .filter((entry) => entry.gate.eligible)
      .map((entry) => ({ ...entry, ...scoreProduct(entry.product, input) }))
      .sort((a, b) =>
        b.score - a.score ||
        b.product.evidenceCount - a.product.evidenceCount ||
        b.product.availableVariants - a.product.availableVariants ||
        Number(a.product.id) - Number(b.product.id)
      )
      .slice(0, MAX_RESULTS);
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function renderResults(container, results) {
    container.replaceChildren();
    if (!results.length) {
      const empty = el("div", "finder-empty");
      empty.append(el("h2", "", "No verified match found"));
      empty.append(el("p", "", "No current product meets every verified requirement selected. Remove an optional preference, browse the collection, or ask Kenlina about an unknown product fact."));
      const link = el("a", "btn btn-outline-primary", "View current collection");
      link.href = "/collections/kenlina-natural-herbal-incense-bracelets";
      empty.append(link);
      container.append(empty);
      return;
    }
    const heading = el("h2", "", "Your verified product paths");
    container.append(heading);
    const grid = el("div", "finder-results-grid");
    results.forEach(({ product, reasons }) => {
      const card = el("article", "finder-result");
      const image = el("img", "finder-result-image");
      image.src = product.image;
      image.alt = product.title;
      image.loading = "lazy";
      image.width = 480;
      image.height = 480;
      card.append(image, el("h3", "", product.title));
      card.append(el("p", "finder-price", `${product.currency} ${(product.price / 100).toFixed(2)}`));
      card.append(el("p", "", reasons.length ? `Why this matched: ${reasons.join("; ")}.` : "Available for manual comparison using current product facts."));
      card.append(el("p", "finder-unknown", `Still unknown or excluded: ${product.unknown.join(", ") || "none for the selected criteria"}.`));
      const productLink = el("a", "btn btn-primary", "Review current product");
      productLink.href = product.url;
      card.append(productLink);
      grid.append(card);
    });
    container.append(grid);
  }

  function readCulturalProfile(values) {
    if (values.get("culture_enabled") !== "on" || typeof window === "undefined" || !window.KenlinaCulturalFinder) return null;
    return window.KenlinaCulturalFinder.buildProfile({
      calendarType: values.get("calendar_type"),
      birthDate: values.get("birth_date"),
      leapMonth: values.get("leap_month"),
      birthTime: values.get("birth_time"),
      birthCity: values.get("birth_city"),
      birthCountry: values.get("birth_country"),
      timeZone: values.get("time_zone"),
    });
  }

  function renderCulturalProfile(container, profile) {
    if (!container) return;
    container.replaceChildren();
    if (!profile) return;
    container.append(el("h3", "", `Input completeness: ${profile.status}`));
    container.append(el("p", "", profile.message));
    if (profile.seasonLabel) container.append(el("p", "", `Optional cultural reference: ${profile.seasonLabel}.`));
    container.append(el("p", "text-muted", profile.disclaimer));
  }

  function readInput(form) {
    const values = new FormData(form);
    return {
      format: values.get("format"),
      wristRoute: values.get("wrist_route") === "wrist",
      wrist: values.get("wrist"),
      wristUnit: values.get("wrist_unit"),
      diameter: values.get("diameter"),
      scent: values.get("scent"),
      care: values.get("care"),
      context: values.get("context"),
      maxBudget: values.get("budget"),
      verifiedOnly: values.get("verified_only") === "on",
      culturalProfile: readCulturalProfile(values),
    };
  }

  function init(section) {
    if (section.dataset.finderReady === "true") return;
    const dataNode = section.querySelector("[data-finder-products]");
    const form = section.querySelector("[data-finder-form]");
    const results = section.querySelector("[data-finder-results]");
    const culturalProfile = section.querySelector("[data-cultural-profile]");
    if (!dataNode || !form || !results) return;
    const products = JSON.parse(dataNode.textContent);
    section.dataset.finderReady = "true";
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const input = readInput(form);
      renderCulturalProfile(culturalProfile, input.culturalProfile);
      renderResults(results, rankProducts(products, input));
      results.focus();
    });
    form.addEventListener("reset", () => {
      results.replaceChildren();
      culturalProfile?.replaceChildren();
      setTimeout(() => form.querySelector("select, input")?.focus(), 0);
    });
  }

  function initAll() {
    document.querySelectorAll("[data-kenlina-product-finder]").forEach(init);
  }

  if (typeof document !== "undefined") {
    initAll();
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initAll);
    document.addEventListener("shopify:section:load", initAll);
  }

  return { diameterCategory, eligibility, normalizeWrist, rankProducts, scoreProduct };
});
